import { entity } from 'simpler-state';

/**
 * Aggregated performance metrics, fed by `flushPerfMetrics` in lib/debug/performance.ts roughly
 * every 2s while `?debug=performance` is on. Purely local to the tab - nothing is sent anywhere.
 *
 * Rows accumulate across windows keyed by metric name: a metric absent from a given window keeps
 * its last-known values rather than disappearing, and each row carries its own `updatedAt` so a
 * reader can tell how stale an entry is.
 */
export interface PerfMetricRow {
  name: string;
  count: number;
  avgMs: number;
  maxMs: number;
  slowCount: number;
  lastMetadata?: Record<string, unknown>;
  updatedAt: number;
}

export type PerfMetricSample = Omit<PerfMetricRow, 'updatedAt'>;

export interface PerfMetricsSnapshot {
  rows: PerfMetricRow[];
  updatedAt: number | null;
}

const INITIAL_STATE: PerfMetricsSnapshot = {
  rows: [],
  updatedAt: null,
};

const store = entity(INITIAL_STATE);

const mergeSnapshot = (samples: PerfMetricSample[], updatedAt: number): void => {
  store.set(current => {
    const byName = new Map<string, PerfMetricRow>();
    current.rows.forEach(row => byName.set(row.name, row));
    samples.forEach(sample => byName.set(sample.name, { ...sample, updatedAt }));

    return {
      rows: Array.from(byName.values()),
      updatedAt,
    };
  });
};

const reset = (): void => {
  store.set(INITIAL_STATE);
};

export default {
  use: () => store.use(state => state),
  get: () => store.get(),
  mergeSnapshot,
  reset,
};
